import { CartUpdatedEvent } from '@theme/events';

const STORAGE_KEY_END = 'fullstack:cart-reservation-timer:end-timestamp';
const STORAGE_KEY_SIGNATURE = 'fullstack:cart-reservation-timer:cart-signature';

/** @type {Set<CartReservationTimer>} */
const activeInstances = new Set();

/** Shared countdown tick so multiple instances stay in sync without racing. */
let sharedIntervalId = null;

/** In-memory end timestamp — survives remounts (cart drawer re-render, theme editor). */
let sharedEndTimestamp = null;

let isClearingCart = false;

function readStoredEnd() {
  const storedEnd = parseInt(localStorage.getItem(STORAGE_KEY_END), 10);
  if (isNaN(storedEnd)) return null;
  return storedEnd;
}

function persistTimer(endTimestamp, signature) {
  sharedEndTimestamp = endTimestamp;
  localStorage.setItem(STORAGE_KEY_END, String(endTimestamp));
  localStorage.setItem(STORAGE_KEY_SIGNATURE, signature);
}

function clearPersistedEnd() {
  sharedEndTimestamp = null;
  localStorage.removeItem(STORAGE_KEY_END);
}

function clearPersistedTimer() {
  clearPersistedEnd();
  localStorage.removeItem(STORAGE_KEY_SIGNATURE);
}

function ensureSharedInterval() {
  if (sharedIntervalId) return;

  sharedIntervalId = setInterval(() => {
    for (const instance of activeInstances) {
      instance.tick();
    }
  }, 1000);
}

function stopSharedIntervalIfIdle() {
  if (activeInstances.size > 0) return;
  if (!sharedIntervalId) return;

  clearInterval(sharedIntervalId);
  sharedIntervalId = null;
}

class CartReservationTimer extends HTMLElement {
  constructor() {
    super();

    this.timeEl = null;
    this.activeEl = null;
    this.expiredEl = null;
    this.durationMs = 0;
    this.cartSignature = '';
    this.clearCartOnExpire = true;
    this.isDesignMode = false;
    this.hasExpired = false;

    this.storageHandler = this.#handleStorageChange.bind(this);
  }

  connectedCallback() {
    this.activeEl = this.querySelector('[data-ref="active"]');
    this.expiredEl = this.querySelector('[data-ref="expired"]');
    this.timeEl = this.querySelector('[data-ref="time"]');

    this.cartSignature = this.dataset.cartSignature || '';
    const durationMinutes = parseInt(this.dataset.durationMinutes, 10) || 15;
    this.durationMs = durationMinutes * 60 * 1000;
    this.clearCartOnExpire = this.dataset.clearCartOnExpire !== 'false';
    this.isDesignMode = window.Shopify?.designMode === true;
    this.hasExpired = false;

    if (!this.cartSignature) {
      // Cart empty: drop reservation so the next add starts a fresh timer.
      if (!this.isDesignMode) clearPersistedTimer();
      return;
    }

    this.#initTimer();

    if (!this.isDesignMode) {
      window.addEventListener('storage', this.storageHandler);
    }
  }

  disconnectedCallback() {
    activeInstances.delete(this);
    stopSharedIntervalIfIdle();

    if (!this.isDesignMode) {
      window.removeEventListener('storage', this.storageHandler);
    }
  }

  /** Public so the shared interval can tick every instance. */
  tick() {
    this.#updateDisplay();
  }

  #initTimer() {
    if (this.isDesignMode) {
      this.#initDesignModeTimer();
      return;
    }

    const now = Date.now();
    const storedEnd = readStoredEnd();
    const storedSignature = localStorage.getItem(STORAGE_KEY_SIGNATURE);
    const memoryEnd = sharedEndTimestamp && sharedEndTimestamp > now ? sharedEndTimestamp : null;
    const validStoredEnd = storedEnd && storedEnd > now ? storedEnd : null;
    const endTimestamp = memoryEnd || validStoredEnd;

    // Resume an in-progress reservation. Do NOT restart when only the cart
    // fingerprint changes (qty / gifts / drawer re-render) — that caused the
    // 15:00 → 14:59 → 15:00 loop when the drawer HTML was replaced.
    if (endTimestamp) {
      persistTimer(endTimestamp, this.cartSignature);
      this.#showActive();
      this.#startTicking();
      return;
    }

    // Timer already finished for this cart contents: keep expired UI.
    // We keep STORAGE_KEY_SIGNATURE after expiry (without END) for this case.
    if (storedSignature === this.cartSignature) {
      this.#handleExpired({ persistSignature: true });
      return;
    }

    this.#startNewTimer();
  }

  #initDesignModeTimer() {
    const now = Date.now();

    if (!sharedEndTimestamp || sharedEndTimestamp <= now) {
      sharedEndTimestamp = now + this.durationMs;
    }

    this.#showActive();
    this.#startTicking();
  }

  #startNewTimer() {
    const now = Date.now();
    const existingEnd = sharedEndTimestamp && sharedEndTimestamp > now ? sharedEndTimestamp : null;
    const storedEnd = readStoredEnd();
    const validStoredEnd = storedEnd && storedEnd > now ? storedEnd : null;
    const endTimestamp = existingEnd || validStoredEnd || now + this.durationMs;

    if (!this.isDesignMode) {
      persistTimer(endTimestamp, this.cartSignature);
    } else {
      sharedEndTimestamp = endTimestamp;
    }

    this.#showActive();
    this.#startTicking();
  }

  #startTicking() {
    activeInstances.add(this);
    this.#updateDisplay();
    ensureSharedInterval();
  }

  #updateDisplay() {
    const endTimestamp = this.#resolveEndTimestamp();
    if (!endTimestamp) return;

    const remaining = endTimestamp - Date.now();

    if (remaining <= 0) {
      this.#handleExpired({ persistSignature: true });
      return;
    }

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    const formatted = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    const timeEl = this.querySelector('[data-ref="time"]') || this.timeEl;
    if (timeEl) timeEl.textContent = formatted;
  }

  #resolveEndTimestamp() {
    if (this.isDesignMode) return sharedEndTimestamp;

    const now = Date.now();

    if (sharedEndTimestamp && sharedEndTimestamp > now) return sharedEndTimestamp;

    const storedEnd = readStoredEnd();
    if (storedEnd && storedEnd > now) {
      sharedEndTimestamp = storedEnd;
      return storedEnd;
    }

    return sharedEndTimestamp;
  }

  #handleExpired({ persistSignature = false } = {}) {
    if (this.hasExpired) return;
    this.hasExpired = true;

    activeInstances.delete(this);
    stopSharedIntervalIfIdle();

    if (!this.isDesignMode) {
      clearPersistedEnd();

      if (persistSignature && this.cartSignature) {
        localStorage.setItem(STORAGE_KEY_SIGNATURE, this.cartSignature);
      } else {
        localStorage.removeItem(STORAGE_KEY_SIGNATURE);
      }
    }

    if (this.clearCartOnExpire) {
      this.#showCountdownAtZero();
      this.#clearCart();
      return;
    }

    this.#showExpired();
  }

  #showCountdownAtZero() {
    const timeEl = this.querySelector('[data-ref="time"]') || this.timeEl;
    if (timeEl) timeEl.textContent = '00:00';
    this.#showActive();
  }

  #showActive() {
    this.activeEl?.classList.remove('hidden');
    this.expiredEl?.classList.add('hidden');
  }

  #showExpired() {
    this.activeEl?.classList.add('hidden');
    this.expiredEl?.classList.remove('hidden');
  }

  async #clearCart() {
    if (this.isDesignMode || isClearingCart) return;

    isClearingCart = true;

    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(`${Theme.routes.cart_url}/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) return;

      const data = await response.json();

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: data.item_count ?? 0,
            source: 'cart-reservation-timer',
            sections: data.sections,
          },
        ),
      );

      const wrapper = this.closest('.cart-reservation-timer');
      if (wrapper) wrapper.dataset.cartEmpty = 'true';
    } catch (error) {
      console.error(error);
    } finally {
      isClearingCart = false;
    }
  }

  #handleStorageChange(event) {
    if (event.key !== STORAGE_KEY_END && event.key !== STORAGE_KEY_SIGNATURE) return;

    // Sync from other tabs — never start a brand-new timer here (avoids reset fights).
    const storedEnd = readStoredEnd();
    const now = Date.now();

    if (storedEnd && storedEnd > now) {
      sharedEndTimestamp = storedEnd;
      this.hasExpired = false;

      if (!activeInstances.has(this)) activeInstances.add(this);

      this.#showActive();
      this.#startTicking();
      return;
    }

    this.#handleExpired({ persistSignature: true });
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
